import React, { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";

const CartCtx = createContext(null);
const STORAGE_KEY = "gta_cart_v1";

/**
 * Cart shape:
 * { items: [ { product_id, name, brand, unit, unit_size, wholesale_price, image_url, quantity, min_order_qty, payment_method } ], notes: "" }
 * Persisted to localStorage.
 *
 * Payment method is a PROPERTY OF EACH PRODUCT (either 'cash' or
 * 'electronic_or_cheque'), never chosen by the customer at checkout. The cart
 * derives a single `requiredPayment` value; if products in the cart disagree
 * we surface a conflict and block checkout.
 */
function readInitial() {
  if (typeof window === "undefined") return { items: [] };
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { items: [] };
    const parsed = JSON.parse(raw);
    if (!parsed?.items || !Array.isArray(parsed.items)) return { items: [] };
    return parsed;
  } catch {
    return { items: [] };
  }
}

// Normalise any legacy product payment_method value to the current 2-value scheme.
function normalizePM(pm) {
  if (pm === "cash_only") return "cash";
  if (pm === "cash") return "cash";
  return "electronic_or_cheque";
}

export function CartProvider({ children }) {
  const [cart, setCart] = useState(readInitial);

  useEffect(() => {
    try { localStorage.setItem(STORAGE_KEY, JSON.stringify(cart)); } catch {}
  }, [cart]);

  const add = useCallback((p, qty = 1) => {
    setCart(prev => {
      const existing = prev.items.find(i => i.product_id === p.id);
      const item = existing
        ? { ...existing, quantity: existing.quantity + qty }
        : {
            product_id: p.id, name: p.name, brand: p.brand || "",
            unit: p.unit || "case",
            unit_size: p.unit_size || "",
            case_size: p.case_size || "",
            wholesale_price: Number(p.wholesale_price) || 0,
            image_url: p.image_url || "",
            min_order_qty: p.min_order_qty || 1,
            payment_method: normalizePM(p.payment_method),
            quantity: qty,
          };
      const items = existing
        ? prev.items.map(i => i.product_id === p.id ? item : i)
        : [...prev.items, item];
      return { ...prev, items };
    });
  }, []);

  const setQty = useCallback((product_id, qty) => {
    setCart(prev => ({
      ...prev,
      items: prev.items
        .map(i => i.product_id === product_id ? { ...i, quantity: Math.max(0, Number(qty) || 0) } : i)
        .filter(i => i.quantity > 0),
    }));
  }, []);

  const remove = useCallback((product_id) => {
    setCart(prev => ({ ...prev, items: prev.items.filter(i => i.product_id !== product_id) }));
  }, []);

  const clear = useCallback(() => setCart({ items: [] }), []);

  const totals = useMemo(() => {
    const subtotal = cart.items.reduce((s, i) => s + i.wholesale_price * i.quantity, 0);
    const units = cart.items.reduce((s, i) => s + i.quantity, 0);
    return { subtotal, units, count: cart.items.length };
  }, [cart.items]);

  // Split payment breakdown — cash + electronic_or_cheque subtotals + line-lists.
  // Mixed carts are ALLOWED; the two subtotals are shown separately everywhere.
  const paymentBreakdown = useMemo(() => {
    const cashItems = [], elecItems = [];
    let cashTotal = 0, elecTotal = 0;
    for (const i of cart.items) {
      const pm = normalizePM(i.payment_method);
      const lineTotal = i.wholesale_price * i.quantity;
      if (pm === "cash") { cashItems.push(i); cashTotal += lineTotal; }
      else { elecItems.push(i); elecTotal += lineTotal; }
    }
    return { cashItems, elecItems, cashTotal, elecTotal, total: cashTotal + elecTotal };
  }, [cart.items]);

  return (
    <CartCtx.Provider value={{ cart, add, setQty, remove, clear, totals, paymentBreakdown }}>
      {children}
    </CartCtx.Provider>
  );
}

export const useCart = () => useContext(CartCtx);
